import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { adminListCategories, adminCreateCategory, adminUpdateCategory, adminDeleteCategory, apiErr } from "@/lib/api";

const slugify = (s) =>
  s.toLocaleLowerCase("tr").replace(/ı/g, "i").replace(/ş/g, "s").replace(/ğ/g, "g")
    .replace(/ü/g, "u").replace(/ö/g, "o").replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function CategoryTable({ cats, reload }) {
  const [rows, setRows] = useState(() => cats.map((c) => ({ ...c })));
  const [neu, setNeu] = useState({ name: "", order: "" });
  const setRow = (i, k, v) => setRows((r) => { const n = [...r]; n[i] = { ...n[i], [k]: v }; return n; });

  const saveRow = async (row) => {
    try {
      await adminUpdateCategory(row.slug, { slug: row.slug, name: row.name, order: Number(row.order) || 99, description: row.description || "" });
      toast.success(`${row.name} güncellendi`);
      reload();
    } catch { toast.error("Güncellenemedi"); }
  };
  const removeRow = async (row) => {
    if (!window.confirm(`"${row.name}" kategorisini silmek istiyor musun? (Ürünler silinmez, sadece kategori listeden kalkar.)`)) return;
    try { await adminDeleteCategory(row.slug); toast.success("Kategori silindi"); reload(); }
    catch { toast.error("Silinemedi"); }
  };
  const create = async () => {
    const name = neu.name.trim();
    if (!name) { toast.error("Kategori adı gerekli"); return; }
    try {
      await adminCreateCategory({ slug: slugify(name), name, order: Number(neu.order) || (rows.length + 1), description: "" });
      toast.success("Kategori eklendi");
      setNeu({ name: "", order: "" });
      reload();
    } catch (e) { toast.error(apiErr(e, "Eklenemedi")); }
  };

  return (
    <div data-testid="category-manager" className="space-y-3">
      <div className="border border-charcoal/10 bg-white">
        <div className="grid grid-cols-[1fr_80px_1.4fr_auto] gap-3 border-b border-charcoal/10 bg-[#faf7f2] px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-charcoal/50">
          <span>Ad</span><span>Sıra</span><span>Açıklama</span><span></span>
        </div>
        {rows.map((row, i) => (
          <div key={row.slug} data-testid={`category-row-${row.slug}`} className="grid grid-cols-[1fr_80px_1.4fr_auto] items-center gap-3 border-b border-charcoal/5 px-4 py-2.5">
            <Input value={row.name} onChange={(e) => setRow(i, "name", e.target.value)} className="h-9" />
            <Input type="number" value={row.order} onChange={(e) => setRow(i, "order", e.target.value)} className="h-9" />
            <Input value={row.description || ""} onChange={(e) => setRow(i, "description", e.target.value)} className="h-9" />
            <div className="flex gap-1">
              <button onClick={() => saveRow(row)} data-testid={`category-save-${row.slug}`} className="rounded p-2 text-charcoal/60 hover:bg-emerald-50 hover:text-emerald-700"><Save className="h-4 w-4" /></button>
              <button onClick={() => removeRow(row)} data-testid={`category-delete-${row.slug}`} className="rounded p-2 text-charcoal/60 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-end gap-3 border border-dashed border-charcoal/20 bg-white p-4">
        <div className="flex-1">
          <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-charcoal/60">Yeni Kategori Adı</span>
          <Input data-testid="new-category-name" value={neu.name} onChange={(e) => setNeu((n) => ({ ...n, name: e.target.value }))} placeholder="örn. Sosis & Salam" />
        </div>
        <div className="w-24">
          <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-charcoal/60">Sıra</span>
          <Input type="number" value={neu.order} onChange={(e) => setNeu((n) => ({ ...n, order: e.target.value }))} />
        </div>
        <Button onClick={create} data-testid="new-category-create" className="bg-bordo text-cream hover:bg-bordo-dark"><Plus className="mr-1 h-4 w-4" /> Ekle</Button>
      </div>
    </div>
  );
}

export default function CategoryManager() {
  const qc = useQueryClient();
  const { data: cats = [], isLoading } = useQuery({ queryKey: ["admin", "categories"], queryFn: adminListCategories, retry: false });
  const reload = () => {
    qc.invalidateQueries({ queryKey: ["admin", "categories"] });
    qc.invalidateQueries({ queryKey: ["categories"] });
  };
  if (isLoading) return <p className="py-10 text-center font-mono text-sm text-charcoal/40">Yükleniyor…</p>;
  return <CategoryTable key={JSON.stringify(cats)} cats={cats} reload={reload} />;
}
